import * as THREE from "three";
import {
  SymbolType,
  WallShapeType,
  WallType,
} from "../types/floorplannerTypes";
import { getQuadraticBezierTangent } from "./arcUtils";
import { getSymbolFootprintSnapPoints } from "./symbolFootprintSnapping";
import { createWallCollisionPolygons } from "../features/floorplan3d/interaction/symbol3DDragCollision";

const EPSILON = 1e-8;
const DEDUPE_DISTANCE = 1e-4;
/** Edges within ~15° are treated as parallel for flush face snaps. */
const PARALLEL_DOT_THRESHOLD = 0.965;

export type SymbolFootprintPolygonFn = (
  symbol: SymbolType,
  position: THREE.Vector2,
) => THREE.Vector2[] | null;

export type SymbolPlacementFeatureSnapInput = {
  symbol: SymbolType;
  position: THREE.Vector2;
  walls: readonly WallType[];
  wallShapesById?: Map<string, WallShapeType[]>;
  defaultWallWidth: number;
  otherSymbols?: readonly SymbolType[];
  tolerance: number;
  /**
   * Preferred footprint source (3D collision polygon / 2D plan polygon).
   * Falls back to property-panel AABB corners when omitted or null.
   */
  getFootprintPolygon?: SymbolFootprintPolygonFn;
  canPlaceAt?: (position: THREE.Vector2) => boolean;
};

type FootprintEdge = {
  start: THREE.Vector2;
  end: THREE.Vector2;
};

/**
 * Magnetically snaps a symbol against nearby wall face corners and other
 * symbols' footprints: corner/mid latch plus parallel edge flush.
 *
 * Returns the original position when nothing is within `tolerance` or the
 * snapped pose would fail `canPlaceAt`.
 */
export function resolveSymbolPlacementFeatureSnap(
  input: SymbolPlacementFeatureSnapInput,
): THREE.Vector2 {
  const { tolerance } = input;
  if (!Number.isFinite(tolerance) || tolerance <= 0) {
    return input.position.clone();
  }

  const movingPolygon = resolveFootprintPolygon(
    input.symbol,
    input.position,
    input.getFootprintPolygon,
  );
  const movingPoints = getFootprintSnapPointsFromPolygon(
    input.symbol.id,
    movingPolygon,
  );
  if (movingPoints.length === 0 && (!movingPolygon || movingPolygon.length < 2)) {
    return input.position.clone();
  }

  const targetPolygons = collectOtherFootprintPolygons(input);
  const pointTargets = collectPlacementSnapTargets(input, targetPolygons);

  let bestDistance = tolerance;
  let bestDelta: THREE.Vector2 | null = null;

  const considerDelta = (delta: THREE.Vector2, distance: number) => {
    if (distance > bestDistance + EPSILON) return;
    if (delta.lengthSq() <= EPSILON * EPSILON) return;
    bestDistance = distance;
    bestDelta = delta.clone();
  };

  // 1) Parallel edge flush — the main “snap against each other” feel.
  if (movingPolygon && movingPolygon.length >= 2) {
    const movingEdges = getPolygonEdges(movingPolygon);
    targetPolygons.forEach(({ polygon }) => {
      getPolygonEdges(polygon).forEach((targetEdge) => {
        movingEdges.forEach((movingEdge) => {
          const flush = computeEdgeFlushDelta(movingEdge, targetEdge, tolerance);
          if (flush) considerDelta(flush.delta, flush.distance);
        });
      });
    });
  }

  // 2) Point latch onto wall corners / footprint corners / edge mids.
  for (const moving of movingPoints) {
    for (const target of pointTargets) {
      const distance = moving.position.distanceTo(target);
      if (distance > bestDistance + EPSILON) continue;
      considerDelta(target.clone().sub(moving.position), distance);
    }
  }

  if (!bestDelta) {
    return input.position.clone();
  }

  const snappedPosition = input.position.clone().add(bestDelta);
  if (input.canPlaceAt && !input.canPlaceAt(snappedPosition)) {
    return input.position.clone();
  }

  return snappedPosition;
}

/**
 * Wall face corners at each endpoint, plus other symbols' footprint corners/mids.
 */
export function collectPlacementSnapTargets(
  input: Pick<
    SymbolPlacementFeatureSnapInput,
    | "symbol"
    | "walls"
    | "wallShapesById"
    | "defaultWallWidth"
    | "otherSymbols"
    | "getFootprintPolygon"
  >,
  otherPolygons?: readonly { symbolId: string; polygon: THREE.Vector2[] }[],
): THREE.Vector2[] {
  const targets: THREE.Vector2[] = [];

  input.walls.forEach((wall) => {
    getWallFaceCornerSnapPoints(
      wall,
      input.defaultWallWidth,
      input.wallShapesById?.get(wall.id),
    ).forEach((point) => pushUniquePoint(targets, point));
  });

  const polygons =
    otherPolygons ?? collectOtherFootprintPolygons(input);
  polygons.forEach(({ symbolId, polygon }) => {
    getFootprintSnapPointsFromPolygon(symbolId, polygon).forEach(({ position }) => {
      pushUniquePoint(targets, position);
    });
  });

  return targets;
}

/**
 * Four outer corners of a wall's end caps (start/end × left/right). For shaped
 * walls, falls back to unique polygon vertices from the collision outline.
 */
export function getWallFaceCornerSnapPoints(
  wall: WallType,
  defaultWallWidth: number,
  wallShapes?: WallShapeType[],
): THREE.Vector2[] {
  const polygons = createWallCollisionPolygons(
    wall,
    defaultWallWidth,
    wallShapes,
  );
  if (wallShapes && wallShapes.length > 0) {
    const points: THREE.Vector2[] = [];
    polygons.forEach((polygon) => {
      polygon.forEach((point) => pushUniquePoint(points, point));
    });
    return points;
  }

  const wallWidth = Math.max(wall.wallWidth ?? defaultWallWidth, EPSILON);
  const halfWidth = wallWidth / 2;
  const startNormal = getWallEndpointNormal(wall, "start");
  const endNormal = getWallEndpointNormal(wall, "end");
  if (!startNormal || !endNormal) return [];

  return [
    wall.start.clone().add(startNormal.clone().multiplyScalar(halfWidth)),
    wall.start.clone().add(startNormal.clone().multiplyScalar(-halfWidth)),
    wall.end.clone().add(endNormal.clone().multiplyScalar(halfWidth)),
    wall.end.clone().add(endNormal.clone().multiplyScalar(-halfWidth)),
  ];
}

function collectOtherFootprintPolygons(
  input: Pick<
    SymbolPlacementFeatureSnapInput,
    "symbol" | "otherSymbols" | "getFootprintPolygon"
  >,
): { symbolId: string; polygon: THREE.Vector2[] }[] {
  const result: { symbolId: string; polygon: THREE.Vector2[] }[] = [];
  input.otherSymbols?.forEach((other) => {
    if (other.id === input.symbol.id) return;
    const polygon = resolveFootprintPolygon(
      other,
      other.position,
      input.getFootprintPolygon,
    );
    if (!polygon || polygon.length < 2) return;
    result.push({ symbolId: other.id, polygon });
  });
  return result;
}

function resolveFootprintPolygon(
  symbol: SymbolType,
  position: THREE.Vector2,
  getFootprintPolygon?: SymbolFootprintPolygonFn,
): THREE.Vector2[] | null {
  if (getFootprintPolygon) {
    const polygon = getFootprintPolygon(symbol, position);
    if (polygon && polygon.length >= 2) return polygon;
  }

  const points = getSymbolFootprintSnapPoints(symbol, position)
    .slice(0, 4)
    .map((point) => point.position);
  return points.length >= 3 ? points : null;
}

/**
 * Vertices plus edge midpoints of a footprint polygon.
 */
export function getFootprintSnapPointsFromPolygon(
  symbolId: string,
  polygon: THREE.Vector2[] | null,
): { symbolId: string; position: THREE.Vector2 }[] {
  if (!polygon || polygon.length < 2) return [];

  const structured: { symbolId: string; position: THREE.Vector2 }[] = [];
  const seen: THREE.Vector2[] = [];
  for (let index = 0; index < polygon.length; index += 1) {
    const current = polygon[index];
    const next = polygon[(index + 1) % polygon.length];
    if (pushUniquePoint(seen, current)) {
      structured.push({ symbolId, position: current.clone() });
    }
    if (current.distanceToSquared(next) <= EPSILON * EPSILON) continue;
    const mid = current.clone().add(next).multiplyScalar(0.5);
    if (pushUniquePoint(seen, mid)) {
      structured.push({ symbolId, position: mid });
    }
  }
  return structured;
}

function getPolygonEdges(polygon: THREE.Vector2[]): FootprintEdge[] {
  const edges: FootprintEdge[] = [];
  for (let index = 0; index < polygon.length; index += 1) {
    const start = polygon[index];
    const end = polygon[(index + 1) % polygon.length];
    if (start.distanceToSquared(end) <= EPSILON * EPSILON) continue;
    edges.push({ start, end });
  }
  return edges;
}

/**
 * If two edges are nearly parallel, overlap along their length, and are within
 * `tolerance` apart, returns the translation that makes them flush.
 */
export function computeEdgeFlushDelta(
  moving: FootprintEdge,
  target: FootprintEdge,
  tolerance: number,
): { delta: THREE.Vector2; distance: number } | null {
  const movingDir = moving.end.clone().sub(moving.start);
  const movingLength = movingDir.length();
  if (movingLength <= EPSILON) return null;
  movingDir.multiplyScalar(1 / movingLength);

  const targetDir = target.end.clone().sub(target.start);
  const targetLength = targetDir.length();
  if (targetLength <= EPSILON) return null;
  targetDir.multiplyScalar(1 / targetLength);

  if (Math.abs(movingDir.dot(targetDir)) < PARALLEL_DOT_THRESHOLD) {
    return null;
  }

  const targetNormal = new THREE.Vector2(-targetDir.y, targetDir.x);
  const movingMid = moving.start.clone().add(moving.end).multiplyScalar(0.5);
  const signedDistance = movingMid.clone().sub(target.start).dot(targetNormal);
  const distance = Math.abs(signedDistance);
  if (distance > tolerance + EPSILON) return null;

  if (!segmentsOverlapOnAxis(moving, target, targetDir)) {
    return null;
  }

  return {
    delta: targetNormal.multiplyScalar(-signedDistance),
    distance,
  };
}

function segmentsOverlapOnAxis(
  a: FootprintEdge,
  b: FootprintEdge,
  axis: THREE.Vector2,
): boolean {
  const a0 = a.start.dot(axis);
  const a1 = a.end.dot(axis);
  const b0 = b.start.dot(axis);
  const b1 = b.end.dot(axis);
  const aMin = Math.min(a0, a1);
  const aMax = Math.max(a0, a1);
  const bMin = Math.min(b0, b1);
  const bMax = Math.max(b0, b1);
  return aMax >= bMin - EPSILON && bMax >= aMin - EPSILON;
}

function getWallEndpointNormal(
  wall: WallType,
  endpoint: "start" | "end",
): THREE.Vector2 | null {
  let tangent: THREE.Vector2;
  if (wall.lineForm === "arc" && wall.controlPoint) {
    const t = endpoint === "start" ? 0 : 1;
    tangent = getQuadraticBezierTangent(
      t,
      wall.start,
      wall.controlPoint,
      wall.end,
    );
  } else {
    tangent = wall.end.clone().sub(wall.start);
  }

  if (tangent.lengthSq() <= EPSILON) return null;
  tangent.normalize();
  return new THREE.Vector2(-tangent.y, tangent.x);
}

function pushUniquePoint(points: THREE.Vector2[], point: THREE.Vector2): boolean {
  if (
    points.some(
      (existing) => existing.distanceToSquared(point) <= DEDUPE_DISTANCE ** 2,
    )
  ) {
    return false;
  }
  points.push(point.clone());
  return true;
}
